import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { format } from 'date-fns';

const ListContainer = styled.div`
  ${tw`space-y-4`}
`;

const CouponCard = styled.div`
  ${tw`bg-white border border-border-default rounded-lg p-4 shadow-card transition`}

  &:hover {
    ${tw`shadow-card`}
  }
`;

const CouponHeader = styled.div`
  ${tw`flex justify-between items-start mb-2`}
`;

const CouponCode = styled.div`
  ${tw`font-mono text-lg font-bold text-green-600`}
`;

const CouponName = styled.div`
  ${tw`text-text-primary font-semibold`}
`;

const CouponDetails = styled.div`
  ${tw`text-sm text-text-secondary space-y-1`}
`;

const ActionButtons = styled.div`
  ${tw`flex gap-2 mt-4`}
`;

const Button = styled.button`
  ${tw`px-3 py-1 rounded-md text-sm transition max-w-full min-w-0 box-border`}
`;

const EditButton = styled(Button)`
  ${tw`bg-black text-white`}

  &:hover {
    ${tw`bg-black`}
  }
`;

const ExpireButton = styled(Button)`
  ${tw`bg-red-500 text-white`}

  &:hover {
    ${tw`bg-red-600`}
  }
`;

const StatusBadge = styled.span<{ active: boolean }>`
  ${tw`px-2 py-1 rounded-md text-xs font-semibold`}
  ${({ active }) =>
    active ? tw`bg-green-100 text-green-800` : tw`bg-bg-secondary text-text-primary`}
`;

interface CouponListProps {
  coupons: any[];
  onEdit: (coupon: any) => void;
  onExpire: (id: string) => void;
}

export default function CouponList({ coupons, onEdit, onExpire }: CouponListProps) {
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'No limit';
    return format(new Date(timestamp), 'MMM dd, yyyy');
  };

  if (coupons.length === 0) {
    return (
      <ListContainer>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          No coupons found. Click "Add Coupon" to create one.
        </div>
      </ListContainer>
    );
  }

  return (
    <ListContainer>
      {coupons.map(coupon => (
        <CouponCard key={coupon.id}>
          <CouponHeader>
            <div>
              <CouponCode>{coupon.code}</CouponCode>
              <CouponName>{coupon.name}</CouponName>
            </div>
            <StatusBadge active={coupon.isActive}>
              {coupon.isActive ? 'Active' : 'Inactive'}
            </StatusBadge>
          </CouponHeader>
          <CouponDetails>
            <div>
              <strong>Category:</strong> {coupon.category}
            </div>
            <div>
              <strong>Algorithm:</strong> {coupon.priceAlgorithm}
              {coupon.priceAlgorithm === 'PERCENTAGE' && coupon.percentageValue
                ? ` (${coupon.percentageValue}%)`
                : coupon.fixAmountValue
                  ? ` ($${coupon.fixAmountValue})`
                  : ''}
            </div>
            <div>
              <strong>Valid from:</strong> {formatDate(coupon.startAt)} to{' '}
              {formatDate(coupon.endAt)}
            </div>
            {coupon.minDays && (
              <div>
                <strong>Minimum days:</strong> {coupon.minDays + 1}
              </div>
            )}
            {coupon.applyToGuestId && (
              <div>
                <strong>Guest:</strong> {coupon.applyToGuest?.email || 'N/A'}
              </div>
            )}
          </CouponDetails>
          <ActionButtons>
            <EditButton onClick={() => onEdit(coupon)}>Edit</EditButton>
            <ExpireButton onClick={() => onExpire(coupon.id)}>Expire</ExpireButton>
          </ActionButtons>
        </CouponCard>
      ))}
    </ListContainer>
  );
}
